// File: src/components/admin/scheduler/SchedulerErrorsPanel.js
// Panel showing recent scheduler errors across all users

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemSecondaryAction,
    IconButton,
    Tooltip,
    Chip,
    Collapse,
    Skeleton,
    Link,
} from '@mui/material';
import {
    Error as ErrorIcon,
    Visibility as ViewIcon,
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon,
} from '@mui/icons-material';

const SchedulerErrorsPanel = ({ errors, loading, expanded, onToggleExpand, onViewUser }) => {
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        const diff = Date.now() - timestamp;
        if (diff < 60000) return 'just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        return new Date(timestamp).toLocaleString();
    };

    const hasErrors = errors && errors.length > 0;

    return (
        <Paper sx={{ mb: 3 }}>
            <Box
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderBottom: expanded ? '1px solid' : 'none',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    bgcolor: hasErrors ? 'error.light' : 'inherit',
                }}
                onClick={onToggleExpand}
            >
                <ErrorIcon color={hasErrors ? 'error' : 'disabled'} />
                <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                    Recent Errors
                    {hasErrors && (
                        <Chip
                            label={errors.length}
                            size="small"
                            color="error"
                            sx={{ ml: 1 }}
                        />
                    )}
                </Typography>
                {hasErrors ? (
                    expanded ? <CollapseIcon /> : <ExpandIcon />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No errors
                    </Typography>
                )}
            </Box>

            <Collapse in={expanded && hasErrors}>
                {loading ? (
                    <Box sx={{ p: 2 }}>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} height={60} sx={{ mb: 1 }} />
                        ))}
                    </Box>
                ) : (
                    <List dense sx={{ py: 0 }}>
                        {errors.slice(0, 10).map((error, index) => (
                            <ListItem
                                key={error.id || index}
                                divider={index < errors.length - 1}
                                sx={{
                                    '&:hover': { bgcolor: 'action.hover' },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <ErrorIcon color="error" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Link
                                                component={RouterLink}
                                                to={`/users/${error.uid}`}
                                                underline="hover"
                                                variant="body2"
                                                fontWeight={500}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {error.email || error.uid || 'Unknown User'}
                                            </Link>
                                            <Chip
                                                label={error.scheduleType || 'Unknown'}
                                                size="small"
                                                variant="outlined"
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                                {formatTimestamp(error.timestamp)}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <Typography
                                            variant="caption"
                                            color="error.main"
                                            sx={{
                                                display: 'block',
                                                maxWidth: '100%',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {error.error || error.message || 'Unknown error'}
                                        </Typography>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <Tooltip title="View User">
                                        <IconButton
                                            size="small"
                                            onClick={() => onViewUser(error.uid)}
                                        >
                                            <ViewIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                )}
                {errors && errors.length > 10 && (
                    <Box sx={{ p: 1, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">
                            Showing 10 of {errors.length} errors
                        </Typography>
                    </Box>
                )}
            </Collapse>
        </Paper>
    );
};

export default SchedulerErrorsPanel;
